import { useCallback, useMemo, useState } from "react"
import type { ProdutoCatalogo } from "@/api"
import type { AppDispatch, RootState } from "@/store"
import { Alert, CircularProgress, Snackbar, Stack } from "@mui/material"
import { useDispatch, useSelector, useStore } from "react-redux"
import { useGetCatalogoQuery, useAdicionarItensComandaMutation, useLazyGetContaTotemCheckoutQuery } from "@/api"
import { capitalize } from "@/lib/formato"
import { uuidLocal } from "@/lib/id"
import { adicionarProdutoComDetalhes, atualizarKiosk, type CartItem } from "@/store/self-service-kiosk-slice"
import { resetarView, trocarView } from "../navegacao-kiosk"
import { reverterTaxaServicoSePendente } from "../items-screen/reverter-taxa-servico"
import AvisoInatividade from "@/components/totem/aviso-inatividade"
import { SOBRE_EMBUTIDO, SobreCardapio } from "@/components/sobre-cardapio"
import ScreenHeader from "../components/screen-header"
import CategoriasLateral from "@/components/catalogo-kiosk/categorias-lateral"
import GradeProdutos, { type CategoriaSelecionada } from "@/components/catalogo-kiosk/grade-produtos"
import { DialogBusca } from "@/components/catalogo-kiosk/dialog-busca"
import DialogDetalheProduto, { type EscolhaDoDetalhe } from "@/components/catalogo-kiosk/dialog-detalhe-produto"
import { temPassos } from "@/components/catalogo-kiosk/passos"
import CardCheckout from "./card-checkout"
import { itemParaPayload } from "./carrinho-totem"
import AddItemsCartBar from "./add-items-cart-bar"
import DialogPassosCheckout from "./dialog-passos-checkout"
import AddItemsReview from "./add-items-review"

const INATIVIDADE_MS = 120_000

function AddItems(): JSX.Element {
    const dispatch = useDispatch<AppDispatch>()
    const store = useStore<RootState>()
    const order = useSelector((state: RootState) => state.selfServiceKiosk.order)
    const modeloIdentificador = useSelector((state: RootState) => state.selfServiceKiosk.modeloIdentificador)
    const identificadorBuscado = useSelector((state: RootState) => state.selfServiceKiosk.identificadorBuscado)

    const { data: catalogo, isLoading: carregandoCatalogo } = useGetCatalogoQuery('CARDAPIO_TOTEM_CHECKOUT')
    const [adicionarItens, { isLoading: enviando }] = useAdicionarItensComandaMutation()
    const [buscarConta] = useLazyGetContaTotemCheckoutQuery()

    const [selectedCategoryId, setSelectedCategoryId] = useState<CategoriaSelecionada>(null)
    const [editingCartItem, setEditingCartItem] = useState<CartItem | null>(null)
    const [erro, setErro] = useState<string | null>(null)
    const [buscaAberta, setBuscaAberta] = useState(false)
    const abrirBusca = useCallback(() => setBuscaAberta(true), [])
    const [produtoDetalhe, setProdutoDetalhe] = useState<ProdutoCatalogo | null>(null)
    // Quantidade escolhida no detalhe, levada à escolha em passos; pelo botão do cartão é sempre 1.
    const [quantidadePassos, setQuantidadePassos] = useState(1)
    const abrirDetalhes = useCallback((produto: ProdutoCatalogo) => setProdutoDetalhe(produto), [])

    const grupos = useMemo(() => catalogo?.grupos ?? [], [catalogo])
    const rotuloPrincipal = capitalize(modeloIdentificador)
    const rotuloOposto = modeloIdentificador === 'COMANDA' ? 'Mesa' : 'Comanda'

    const renderCard = useCallback(
        (produto: ProdutoCatalogo, cor: string | null) => <CardCheckout key={produto.id} produto={produto} cor={cor} aoAbrirDetalhes={abrirDetalhes} />,
        [abrirDetalhes],
    )

    const voltar = (): void => {
        trocarView(dispatch, { currentView: 'VIEW_ITEMS' })
    }

    const voltarAoInicio = useCallback(() => {
        reverterTaxaServicoSePendente(dispatch, order)
        resetarView(dispatch)
    }, [dispatch, order])

    const fecharStepDialog = (): void => {
        dispatch(atualizarKiosk({ stepDialogProduct: null, showReviewItems: Boolean(editingCartItem) }))
        setEditingCartItem(null)
        setQuantidadePassos(1)
    }

    // Produto simples vai direto ao carrinho; com opções, segue para a escolha em passos levando a quantidade.
    const confirmarDetalhe = ({ quantidade, observacao }: EscolhaDoDetalhe): void => {
        if (!produtoDetalhe) return

        setProdutoDetalhe(null)

        if (temPassos(produtoDetalhe)) {
            setQuantidadePassos(quantidade)
            dispatch(atualizarKiosk({ stepDialogProduct: produtoDetalhe }))
            return
        }

        dispatch(adicionarProdutoComDetalhes({
            idProduto: produtoDetalhe.id,
            nome: produtoDetalhe.descricao,
            valorUnitario: produtoDetalhe.valor,
            faixasPreco: produtoDetalhe.faixasPreco,
            quantidade,
            observacao,
        }))
    }

    const editarItem = useCallback((item: CartItem): void => {
        const produto = grupos.flatMap((g) => g.produtos).find((p) => p.id === item.idProduto)
        if (!produto) {
            setErro('Não foi possível localizar o produto para edição.')
            return
        }

        setEditingCartItem(item)
        dispatch(atualizarKiosk({ stepDialogProduct: produto, showReviewItems: false }))
    }, [grupos, dispatch])

    const confirmar = useCallback(async (): Promise<void> => {
        const estado = store.getState().selfServiceKiosk
        const cartItems = estado.cartItems
        if (cartItems.length === 0) return

        const chaveIdempotencia = estado.chaveEnvioItens ?? uuidLocal()
        if (!estado.chaveEnvioItens) dispatch(atualizarKiosk({ chaveEnvioItens: chaveIdempotencia }))

        try {
            await adicionarItens({
                identificador: identificadorBuscado,
                itens: cartItems.map(itemParaPayload),
                idPedidoIntegracao: order?.idPedidoIntegracao ?? undefined,
                chaveIdempotencia,
            }).unwrap()

            // Zera o aviso de preço aqui: os dois botões de "Adicionar ao pedido" caem neste envio.
            dispatch(atualizarKiosk({ cartItems: [], showReviewItems: false, chaveEnvioItens: null, alteracoesPreco: [] }))

            const conta = await buscarConta({ identificador: identificadorBuscado }).unwrap()
            const pedidos = conta.pedidos ?? []

            const selecionado = order?.idPedidoIntegracao ? pedidos.find((p) => p.idPedidoIntegracao === order.idPedidoIntegracao) : undefined
            if (selecionado) {
                trocarView(dispatch, {
                    currentView: 'VIEW_ITEMS',
                    order: selecionado,
                    multipleOrders: pedidos,
                    hasMultipleOrders: pedidos.length > 1,
                })
            } else if (pedidos.length > 1) {
                trocarView(dispatch, { currentView: 'VIEW_MULTIPLE_ORDERS', multipleOrders: pedidos, hasMultipleOrders: true })
            } else {
                trocarView(dispatch, { currentView: 'VIEW_ITEMS', order: pedidos[0] ?? null, hasMultipleOrders: false })
            }
        } catch (err) {
            console.error('Erro ao adicionar itens à comanda:', err)
            const mensagem = (err as { data?: { mensagem?: string } })?.data?.mensagem
            setErro(mensagem ?? 'Não foi possível adicionar os itens ao pedido. Tente novamente.')
        }
    }, [store, adicionarItens, buscarConta, identificadorBuscado, order, dispatch])

    return (
        <Stack sx={{ width: '100%', height: '100vh', overflow: 'hidden', boxSizing: 'border-box' }}>
            <ScreenHeader
                title="Adicionar item"
                subTitle={`${rotuloPrincipal} ${identificadorBuscado}${order?.identificadorSecundario ? ` · ${rotuloOposto} ${order.identificadorSecundario}` : ''}`}
                onClickBack={voltar}
                voltarDesabilitado={enviando}
            />

            {carregandoCatalogo ? (
                <Stack sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />

                    {/* Sem a coluna de categorias enquanto carrega, o "Sobre" flutua. */}
                    <SobreCardapio />
                </Stack>
            ) : (
                <Stack
                    direction="row"
                    aria-busy={enviando}
                    sx={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        transition: 'opacity 0.2s',
                        opacity: enviando ? 0.5 : 1,
                        pointerEvents: enviando ? 'none' : 'auto',
                    }}
                >
                    <CategoriasLateral
                        grupos={grupos}
                        selectedCategoryId={selectedCategoryId}
                        onCategorySelect={setSelectedCategoryId}
                        rodape={SOBRE_EMBUTIDO}
                    />

                    <GradeProdutos
                        grupos={grupos}
                        selectedCategoryId={selectedCategoryId}
                        renderCard={renderCard}
                        aoBuscar={abrirBusca}
                    />
                </Stack>
            )}

            <AddItemsCartBar onConfirm={confirmar} enviando={enviando} />

            <DialogBusca aberto={buscaAberta} grupos={grupos} renderCard={renderCard} aoFechar={() => setBuscaAberta(false)} />

            <DialogDetalheProduto produto={produtoDetalhe} aparelho="totem" onFechar={() => setProdutoDetalhe(null)} onConfirmar={confirmarDetalhe} />

            <DialogPassosCheckout editingCartItem={editingCartItem} onClose={fecharStepDialog} quantidade={quantidadePassos} />

            <AddItemsReview onConfirm={confirmar} onEditItem={editarItem} enviando={enviando} />

            <AvisoInatividade ativo={!enviando} msInatividade={INATIVIDADE_MS} aoExpirar={voltarAoInicio} />

            <Snackbar
                open={!!erro}
                autoHideDuration={5000}
                onClose={() => setErro(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="error" variant="filled" onClose={() => setErro(null)} sx={{ fontSize: '1.1rem' }}>
                    {erro}
                </Alert>
            </Snackbar>
        </Stack>
    )
}

export default AddItems
